import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Col, Form, Row, Spinner, Alert, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';

const TabAsignarPlan = ({ idRNC, vNombre, vRNC, idClienteAkasia, onSuccess, onCancel }) => {
const { token } = useToken();
const { showSuccessToast, showErrorToast } = useToast();
    
const [planes, setPlanes] = useState([]);
const [ordenes, setOrdenes] = useState([]);
const [cargandoPlanes, setCargandoPlanes] = useState(false);
const [guardando, setGuardando] = useState(false);
const [planSeleccionado, setPlanSeleccionado] = useState(null);
const [ordenSeleccionada, setOrdenSeleccionada] = useState('');
const [fechaInicio, setFechaInicio] = useState(new Date().toISOString().split('T')[0]);
const [fechaFin, setFechaFin] = useState('');

useEffect(() => {
    if (idRNC) {
        ObtenerPlanes();
        ObtenerOrdenes();
    }
}, [idRNC, vNombre, vRNC, idClienteAkasia]);

    useEffect(() => {
        if (planSeleccionado && fechaInicio) {
            calcularFechaFin();
        }
    }, [planSeleccionado, fechaInicio]);


    const ObtenerOrdenes = async () => {
        try {

            const url = `/api/FacturaRepublicaDominicana/ObtenerOrdenesConPlanesNoAsignado?idClienteAkasia=${encodeURIComponent(idClienteAkasia)}`;

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();

                if (data.iResponseType === 1) {
                    const ordenesData = JSON.parse(data.detalles);
                    setOrdenes(ordenesData);
                } else {
                    setOrdenes([]);
                }
            } else {
                setOrdenes([]);
            }
        } catch (error) {
            console.error('Error:', error);
            setOrdenes([]);
        }
    };

    const ObtenerPlanes = async () => {
        setCargandoPlanes(true);
        
        try {
            const response = await fetch('/api/FacturaRepublicaDominicana/ObtenerCatalogoPlanes', {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                
                if (data.iResponseType === 1) {
                    const planesData = JSON.parse(data.detalles);
                    setPlanes(planesData);
                } else {
                    showErrorToast(data.vMessage || 'Error al obtener planes');
                    setPlanes([]);
                }
            } else {
                showErrorToast('Error al cargar planes');
                setPlanes([]);
            }
        } catch (error) {
            console.error('Error:', error);
            showErrorToast('Error al cargar planes');
            setPlanes([]);
        } finally {
            setCargandoPlanes(false);
        }
    };

    const calcularFechaFin = () => {
        if (!planSeleccionado || !fechaInicio) return;
        
        const inicio = new Date(fechaInicio);
        const duracionDias = planSeleccionado.dDias || 30;
        const fin = new Date(inicio);
        fin.setDate(fin.getDate() + duracionDias);
        
        setFechaFin(fin.toISOString().split('T')[0]);
    };

    const AsignarPlan = async () => {
        if (!planSeleccionado) {
            showErrorToast('Debe seleccionar un plan');
            return;
        }

        if (!fechaInicio) {
            showErrorToast('Debe especificar la fecha de inicio');
            return;
        }

        if (!ordenSeleccionada) {
            showErrorToast('Debe seleccionar una orden de pago');
            return;
        }

        setGuardando(true);

        const data = {
            idRepublicaDominicanaFacturacionExistencias: idRNC,
            idPlanesFacturacionRepublicaDominicana: planSeleccionado.idRepublicaDominicanaPlanesFacturacion,
            idOrdenPago: ordenSeleccionada,
            dFechaInicio: fechaInicio,
            dFechaFin: fechaFin
        };

        try {
            const response = await fetch('/api/FacturaRepublicaDominicana/AsignarPlan/?jsonPlan=' + encodeURIComponent(JSON.stringify(data)), {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const result = await response.json();

                if (result.iResponseType === 1) {
                    showSuccessToast('Plan asignado exitosamente');
                    setPlanSeleccionado(null);
                    setOrdenSeleccionada('');
                    setFechaInicio(new Date().toISOString().split('T')[0]);
                    setFechaFin('');
                    if (onSuccess) onSuccess();
                } else {
                    showErrorToast(result.vMessage || 'Error al asignar plan');
                }
            } else {
                showErrorToast('Error al asignar plan');
            }
        } catch (error) {
            console.error('Error:', error);
            showErrorToast('Error al asignar plan');
        } finally {
            setGuardando(false);
        }
    };

    const handlePlanChange = (e) => {
        const planId = parseInt(e.target.value);
        const plan = planes.find(p => p.idRepublicaDominicanaPlanesFacturacion === planId);
        setPlanSeleccionado(plan);
    };

    const calcularDiasRestantes = () => {
        if (!fechaInicio || !fechaFin) return 0;
        
        const inicio = new Date(fechaInicio);
        const fin = new Date(fechaFin);
        const diferencia = fin - inicio;
        
        return Math.ceil(diferencia / (1000 * 60 * 60 * 24));
    };

    if (cargandoPlanes) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted">Cargando información...</p>
            </div>
        );
    }

    return (
        <div className="mb-4">
            <Alert variant="info" className="mb-4">
                <FontAwesomeIcon icon="info-circle" className="me-2" />
                <small>
                    Seleccione el plan, la orden de pago y configure las fechas de vigencia
                </small>
            </Alert>

            {/* Información del RNC (proporcionada por props) */}
            {(vNombre || vRNC) && (
                <Card className="border-0 bg-light mb-3">
                    <Card.Body>
                        <h6 className="text-primary mb-3">
                            <FontAwesomeIcon icon="building" className="me-2" />
                            Información del RNC
                        </h6>

                        <Row className="mb-2">
                            <Col xs={4}>
                                <small className="text-muted">RNC:</small>
                            </Col>
                            <Col xs={8}>
                                <strong>
                                    <code className="bg-white px-2 py-1 rounded">{vRNC}</code>
                                </strong>
                            </Col>
                        </Row>

                        <Row>
                            <Col xs={4}>
                                <small className="text-muted">Razón Social:</small>
                            </Col>
                            <Col xs={8}>
                                <strong>{vNombre}</strong>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            )}

            <Form>
                <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold small">Plan *</Form.Label>
                    <Form.Select
                        size="sm"
                        value={planSeleccionado?.idRepublicaDominicanaPlanesFacturacion || ''}
                        onChange={handlePlanChange}
                    >
                        <option value="">Seleccione un plan...</option>
                        {planes.map((plan) => (
                            <option 
                                key={plan.idRepublicaDominicanaPlanesFacturacion} 
                                value={plan.idRepublicaDominicanaPlanesFacturacion}
                            >
                                {plan.vPlanTitle} - ${plan.dPlanPrice.toFixed(2)} ({plan.dCantidad === -1 ? 'Ilimitado' : plan.dCantidad} folios)
                            </option>
                        ))}
                    </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold small">
                        <FontAwesomeIcon icon="file-invoice-dollar" className="me-1" />
                        Orden de Pago *
                    </Form.Label>
                    <Form.Select
                        size="sm"
                        value={ordenSeleccionada}
                        onChange={(e) => setOrdenSeleccionada(e.target.value)}
                    >
                        <option value="">Seleccione una orden...</option>
                        {ordenes.map((orden) => (
                            <option 
                                key={orden.idOrden} 
                                value={orden.idOrden}
                            >
                                {`Orden ${orden.idOrden} - ${orden.vMoneda || ''} ${Number(orden.dTotal || 0).toFixed(2)} (${new Date(orden.dFechaVenta).toLocaleDateString('es-DO')})`}
                            </option>
                        ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                        Seleccione la orden que avala el pago de este plan
                    </Form.Text>
                </Form.Group>

                {planSeleccionado && (
                    <>
                        <Card className="border-0 bg-light mb-3">
                            <Card.Body>
                                <h6 className="text-primary mb-3">
                                    <FontAwesomeIcon icon="info-circle" className="me-2" />
                                    Detalles del Plan
                                </h6>
                                
                                <Row className="mb-2">
                                    <Col xs={6}>
                                        <small className="text-muted">Nombre:</small>
                                    </Col>
                                    <Col xs={6} className="text-end">
                                        <strong>{planSeleccionado.vPlanTitle}</strong>
                                    </Col>
                                </Row>

                                <Row className="mb-2">
                                    <Col xs={6}>
                                        <small className="text-muted">Precio:</small>
                                    </Col>
                                    <Col xs={6} className="text-end">
                                        <strong className="text-success">${planSeleccionado.dPlanPrice.toFixed(2)}</strong>
                                    </Col>
                                </Row>

                                <Row className="mb-2">
                                    <Col xs={6}>
                                        <small className="text-muted">Folios incluidos:</small>
                                    </Col>
                                    <Col xs={6} className="text-end">
                                        <strong>{planSeleccionado.dCantidad === -1 ? 'Ilimitado' : planSeleccionado.dCantidad.toLocaleString()}</strong>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col xs={6}>
                                        <small className="text-muted">Duración:</small>
                                    </Col>
                                    <Col xs={6} className="text-end">
                                        <strong>{planSeleccionado.dDias || 30} días</strong>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-semibold small">
                                        <FontAwesomeIcon icon="calendar-plus" className="me-1" />
                                        Fecha de Inicio *
                                    </Form.Label>
                                    <Form.Control
                                        size="sm"
                                        type="date"
                                        value={fechaInicio}
                                        onChange={(e) => setFechaInicio(e.target.value)}
                                        min={new Date().toISOString().split('T')[0]}
                                    />
                                    <Form.Text className="text-muted">
                                        Inicio de vigencia del plan
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-semibold small">
                                        <FontAwesomeIcon icon="calendar-check" className="me-1" />
                                        Fecha de Fin
                                    </Form.Label>
                                    <Form.Control
                                        size="sm"
                                        type="date"
                                        value={fechaFin}
                                        readOnly
                                        className="bg-light"
                                    />
                                    <Form.Text className="text-muted">
                                        Calculado automáticamente
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Card className="border-0 bg-primary bg-opacity-10 mb-4">
                            <Card.Body>
                                <h6 className="text-primary mb-3">
                                    <FontAwesomeIcon icon="calendar-alt" className="me-2" />
                                    Vigencia del Plan
                                </h6>
                                
                                <Row className="mb-2">
                                    <Col xs={6}>
                                        <small className="text-muted">
                                            <FontAwesomeIcon icon="calendar-plus" className="me-1" />
                                            Inicia:
                                        </small>
                                    </Col>
                                    <Col xs={6} className="text-end">
                                        <strong>{fechaInicio ? new Date(fechaInicio + 'T00:00:00').toLocaleDateString('es-DO', { 
                                            weekday: 'short',
                                            year: 'numeric', 
                                            month: 'short', 
                                            day: 'numeric' 
                                        }) : '-'}</strong>
                                    </Col>
                                </Row>

                                <Row className="mb-2">
                                    <Col xs={6}>
                                        <small className="text-muted">
                                            <FontAwesomeIcon icon="calendar-check" className="me-1" />
                                            Finaliza:
                                        </small>
                                    </Col>
                                    <Col xs={6} className="text-end">
                                        <strong>{fechaFin ? new Date(fechaFin + 'T00:00:00').toLocaleDateString('es-DO', { 
                                            weekday: 'short',
                                            year: 'numeric', 
                                            month: 'short', 
                                            day: 'numeric' 
                                        }) : '-'}</strong>
                                    </Col>
                                </Row>

                                <Row className="pt-2 border-top">
                                    <Col xs={6}>
                                        <small className="text-muted">
                                            <FontAwesomeIcon icon="clock" className="me-1" />
                                            Días de vigencia:
                                        </small>
                                    </Col>
                                    <Col xs={6} className="text-end">
                                        <h5 className="mb-0 text-primary">{calcularDiasRestantes()} días</h5>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        <Card className="border-0 bg-success bg-opacity-10 mb-4">
                            <Card.Body>
                                <Row className="align-items-center">
                                    <Col xs={6}>
                                         <h6 className="mb-0">
                                            <FontAwesomeIcon icon="dollar-sign" className="me-2" />
                                            Total a pagar:
                                        </h6>
                                    </Col>
                                    <Col xs={6} className="text-end">
                                        <h4 className="mb-0 text-success">${planSeleccionado.dPlanPrice.toFixed(2)}</h4>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </>
                )}

                <div className="d-grid gap-2">
                    <Button
                        variant="success"
                        size="sm"
                        onClick={AsignarPlan}
                        disabled={guardando || !planSeleccionado || !fechaInicio || !ordenSeleccionada}
                    >
                        {guardando ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Asignando...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon="check" className="me-2" />
                                Asignar Plan
                            </>
                        )}
                    </Button>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={onCancel}
                        disabled={guardando}
                    >
                        <FontAwesomeIcon icon="times" className="me-2" />
                        Cancelar
                    </Button>
                </div>
            </Form>
        </div>
    );
};

TabAsignarPlan.propTypes = {
    idRNC: PropTypes.number,
    vNombre: PropTypes.string,
    vRNC: PropTypes.string,
    idClienteAkasia: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onSuccess: PropTypes.func,
    onCancel: PropTypes.func
};

export default TabAsignarPlan;
